import React, {
  type ComponentProps,
  createContext,
  useContext,
  useMemo,
} from 'react'

import { NeutralColorToken } from '../themes'
import { PrimaryColorOverride } from './PrimaryColorOverride'

const AchromaticThemeContext = createContext<{ isAchromaticTheme: boolean }>({
  isAchromaticTheme: false,
})
export function useIsAchromaticTheme() {
  return useContext(AchromaticThemeContext).isAchromaticTheme
}

type Props = {
  /**
   * Children to render. This is a render function that will be passed the following props:
   *
   * * rootClassName (string): The CSS class name to apply to your root child element.
   * * __experimentalCssString (string): The CSS properties applied by `rootClassName`, in
   *      their raw string form. You would use this if you wanted to apply them to the DOM
   *      yourself, but you generally shouldn't do that.
   */
  children: ComponentProps<typeof PrimaryColorOverride>['children']
  /**
   * Optionally disable this override, for cases where we want to conditionally apply achromatic theming.
   */
  isDisabled?: boolean
}

/**
 * An `AchromaticThemeOverride` overrides the current theme’s primary color for any child
 * components. It will also force all components within its children to use their corresponding
 * styles, such as `TextButton` using the new achromatic underlined style.
 *
 * This component affects both the React and DOM subtrees, which means that its effects will
 * carry over across Portals.
 */
export function AchromaticThemeOverride({ children, isDisabled }: Props) {
  const contextValue = useMemo(() => ({ isAchromaticTheme: true }), [])

  if (isDisabled) {
    return <>{children({ __experimentalCssString: '', rootClassName: '' })}</>
  }

  return (
    <AchromaticThemeContext.Provider value={contextValue}>
      <PrimaryColorOverride color={NeutralColorToken.Fg1}>
        {children}
      </PrimaryColorOverride>
    </AchromaticThemeContext.Provider>
  )
}
